<template>
  <b-modal v-model="localVisible" title="Form Submission Rules" @ok="onSave" @hide="onHide" size="xl">
    <template #title>
      Form Submission Rules
      <modal-help topic="submission-rules" />
    </template>
    <div class="sre-container">
      <!-- Rule chain display -->
      <div v-if="localRules.length" class="sre-rule-list">
        <template v-for="(rule, idx) in localRules" :key="rule.id">
          <div v-if="idx > 0" class="sre-connector">
            <span class="sre-connector-line"></span>
          </div>
          <div class="sre-rule-item" :class="{ 'sre-rule-editing': editingIndex === idx }">
            <button type="button" class="sre-rule-open" @click="editRule(idx)">
            <span class="sre-type-badge" :class="'sre-type-' + rule.type">{{ rule.type.toUpperCase() }}</span>
            <span class="sre-rule-body">
              <template v-if="rule.type !== 'else'">
                <span class="sre-rule-conditions">
                  <template v-for="(cond, ci) in rule.conditions" :key="ci">
                    <span v-if="ci > 0" class="sre-cond-join">{{ rule.conditionMatchMode === 'any' ? 'OR' : 'AND' }}</span>
                    <span class="sre-cond-text">{{ conditionSummary(cond) }}</span>
                  </template>
                  <span v-if="!rule.conditions || !rule.conditions.length" class="sre-cond-empty">(no conditions)</span>
                </span>
              </template>
              <span class="sre-rule-arrow">→</span>
              <span class="sre-rule-actions">
                <span v-for="(action, ai) in rule.actions" :key="ai" class="sre-action-pill">
                  {{ actionSummary(action) }}
                </span>
                <span v-if="!rule.actions || !rule.actions.length" class="sre-action-empty">no actions</span>
              </span>
            </span>
            </button>
            <button type="button" class="sre-rule-remove" @click.stop="removeRule(idx)" aria-label="Remove rule">&times;</button>
          </div>
        </template>
      </div>

      <!-- Empty state -->
      <div v-if="!localRules.length && !isEditing" class="sre-empty">
        No submission rules configured. The form will submit data without any post-submission behavior.
      </div>

      <!-- Edit form -->
      <div v-if="isEditing" class="sre-edit-form">
        <div class="sre-edit-title">{{ editingIndex !== null ? 'Edit' : 'New' }} Rule</div>

        <!-- Rule type: hidden when the chain leaves only one choice (a lone IF, or ELSE IF after ELSE) -->
        <div v-if="availableTypes.length > 1" class="sre-form-row">
          <label class="sre-form-label">Rule Type</label>
          <select v-model="editType" class="sre-select">
            <option v-for="opt in availableTypes" :key="opt" :value="opt">{{ opt.toUpperCase() }}</option>
          </select>
        </div>

        <!-- Conditions (not for else) -->
        <template v-if="editType !== 'else'">
          <div class="sre-form-section">
            <div class="sre-section-header">
              <span class="sre-section-title">Conditions</span>
              <div v-if="editConditions.length > 1" class="sre-match-toggle">
                <button class="sre-mt-btn" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">ALL</button>
                <button class="sre-mt-btn" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">ANY</button>
              </div>
            </div>

            <div v-for="(cond, ci) in editConditions" :key="ci" class="sre-condition-row">
              <select v-model="cond.source" @change="onCondSourceChange(cond)" class="sre-select sre-select-sm">
                <option value="self">Form field</option>
                <option value="field">Another field</option>
                <option value="related">Related field</option>
                <option value="modal">Page is open in a modal</option>
                <option value="none">Always (no condition)</option>
              </select>

              <select v-if="cond.source === 'self' || cond.source === 'field'" v-model="cond.field" @change="onCondFieldChange(cond)" class="sre-select sre-select-sm">
                <option value="" disabled>Select field...</option>
                <option v-for="col in directColumns" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
              </select>

              <template v-if="cond.source === 'related'">
                <select v-model="cond.relationshipColumn" @change="onCondRelColChange(cond)" class="sre-select sre-select-sm">
                  <option value="" disabled>Relationship...</option>
                  <option v-for="rel in relationshipColumns" :key="rel.uniqueTitle" :value="rel.uniqueTitle">{{ rel.title || rel.uniqueTitle }}</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.relatedQuantifier" class="sre-select sre-select-sm sre-quantifier">
                  <option value="any">any</option>
                  <option value="all">all</option>
                  <option value="none">none of</option>
                </select>
                <select v-if="cond.relationshipColumn" v-model="cond.field" class="sre-select sre-select-sm">
                  <option value="" disabled>Field...</option>
                  <option v-for="col in getRelatedColumns(cond.relationshipColumn)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title || col.uniqueTitle }}</option>
                </select>
              </template>

              <template v-if="cond.source !== 'none' && cond.source !== 'modal'">
                <select v-model="cond.operator" class="sre-select sre-select-sm">
                  <option v-for="op in operatorsForDataType(cond.dataType || 'string')" :key="op.key" :value="op.key">{{ op.label }}</option>
                </select>

                <template v-if="!isNoValueOp(cond.operator)">
                  <!-- Relationship record picker -->
                  <template v-if="condFieldIsRelationship(cond)">
                    <div class="sre-rel-picker">
                      <div v-if="cond.relSelectedRecords && cond.relSelectedRecords.length" class="sre-rel-tags">
                        <span v-for="rec in cond.relSelectedRecords" :key="rec.id" class="sre-rel-tag">
                          {{ rec.displayValue }}
                          <button class="sre-rel-tag-remove" @click="removeCondRelRecord(cond, rec.id)">&times;</button>
                        </span>
                      </div>
                      <div class="sre-rel-search-wrap">
                        <input
                          type="text"
                          v-model="cond.relSearchQuery"
                          class="sre-input sre-input-sm"
                          placeholder="Search records..."
                          @input="onCondRelSearchInput(cond)"
                        />
                        <div v-if="cond.relSearchLoading" class="sre-rel-loading">searching...</div>
                      </div>
                      <div v-if="cond.relSearchResults && cond.relSearchResults.length" class="sre-rel-results">
                        <div
                          v-for="rec in cond.relSearchResults"
                          :key="rec.id"
                          class="sre-rel-result-item"
                          @click="selectCondRelRecord(cond, rec)"
                        >{{ rec.displayValue || '(no value)' }}</div>
                      </div>
                    </div>
                  </template>
                  <input
                    v-else-if="cond.dataType === 'number'"
                    v-model.number="cond.value"
                    class="sre-input sre-input-sm"
                    type="number"
                    step="any"
                    placeholder="0"
                  />
                  <input
                    v-else-if="cond.dataType === 'datetime'"
                    v-model="cond.value"
                    class="sre-input sre-input-sm"
                    type="datetime-local"
                  />
                  <select
                    v-else-if="cond.dataType === 'checkbox'"
                    v-model="cond.value"
                    class="sre-select sre-select-sm"
                  >
                    <option value="true">Checked</option>
                    <option value="false">Unchecked</option>
                  </select>
                  <input
                    v-else
                    v-model="cond.value"
                    class="sre-input sre-input-sm"
                    type="text"
                    placeholder="value..."
                  />
                </template>
              </template>

              <button class="sre-cond-remove" @click="removeCondition(ci)" title="Remove condition">&times;</button>
            </div>

            <button class="sre-add-btn" @click="addCondition">+ add condition</button>
          </div>
        </template>

        <!-- Submission Actions -->
        <div class="sre-form-section">
          <div class="sre-section-header">
            <span class="sre-section-title">Submission Actions</span>
          </div>

          <div v-for="(action, ai) in editActions" :key="ai" class="sre-action-row">
            <select v-model="action.type" @change="onActionTypeChange(action)" class="sre-select sre-select-sm">
              <option value="" disabled>Action...</option>
              <option v-for="opt in submissionActionOptions" :key="opt.key" :value="opt.key">{{ opt.label }}</option>
            </select>

            <!-- Redirect to specific page -->
            <template v-if="action.type === 'redirect_page'">
              <select v-model="action.pageUniqueTitle" class="sre-select sre-select-sm">
                <option value="" disabled>Select page...</option>
                <option v-for="page in pages" :key="page.uniqueTitle" :value="page.uniqueTitle">{{ page.title || page.uniqueTitle }}</option>
              </select>
            </template>

            <!-- Redirect to child page -->
            <template v-if="action.type === 'redirect_child'">
              <select v-model="action.pageUniqueTitle" class="sre-select sre-select-sm">
                <option v-if="!action.autoCreate" value="" disabled>Select child page...</option>
                <option v-else value="">Create a new sub-page</option>
                <option v-for="page in childPages" :key="page.uniqueTitle" :value="page.uniqueTitle">{{ page.title || page.uniqueTitle }}</option>
              </select>
              <label class="sre-checkbox-label">
                <input type="checkbox" v-model="action.autoCreate" @change="onAutoCreateToggle(action)" />
                Auto-create child page
              </label>
              <template v-if="action.autoCreate && !action.pageUniqueTitle">
                <input
                  v-model="action.newPageTitle"
                  class="sre-input sre-input-sm"
                  type="text"
                  placeholder="sub-page title"
                  @input="onNewChildPageTitleInput(action)"
                />
                <input
                  v-model="action.newPageUniqueTitle"
                  class="sre-input sre-input-sm"
                  type="text"
                  placeholder="sub_page_unique_title"
                  @input="action.newPageUniqueTitleManual = true"
                />
                <span class="sre-hint">Created as a sub-page of {{ currentPageLabel }}</span>
                <span v-if="action.createError" class="sre-hint sre-hint-error">{{ action.createError }}</span>
              </template>
            </template>

            <!-- Clear form with optional message -->
            <template v-if="action.type === 'clear_form'">
              <label class="sre-checkbox-label">
                <input type="checkbox" v-model="action.showMessage" />
                Show message
              </label>
              <input
                v-if="action.showMessage"
                v-model="action.message"
                class="sre-input sre-input-sm sre-input-wide"
                type="text"
                placeholder="Custom success message..."
              />
            </template>

            <template v-if="action.type === 'refresh_view'">
              <select v-model="action.viewUniqueTitle" class="sre-select sre-select-sm">
                <option value="" disabled>Select view...</option>
                <option v-for="view in pageViewOptions" :key="view.uniqueTitle" :value="view.uniqueTitle">{{ view.label }}</option>
              </select>
              <span v-if="!pageViewOptions.length" class="sre-hint">No tables, cards, or charts on this page</span>
            </template>

            <span v-if="action.type === 'close_modal'" class="sre-hint">Closes the dialog. A direct visit to this page does nothing.</span>
            <span v-if="action.type === 'close_modal_reload'" class="sre-hint">Closes the dialog and reloads the page behind it, including its views and menus. A direct visit reloads this page.</span>

            <template v-if="action.type === 'set_related_value' || action.type === 'create_related'">
              <select :value="relationshipKey(action)" class="sre-select sre-select-sm" @change="setRelationship(action, $event.target.value)">
                <option value="" disabled>Relationship...</option>
                <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
              </select>
              <span v-if="!relationshipTargets.length" class="sre-hint">This spreadsheet has no relationships</span>
            </template>

            <template v-if="action.type === 'set_related_value' && action.relatedSpreadSheet">
              <select v-model="action.field" class="sre-select sre-select-sm" @change="onTargetFieldChange(action)">
                <option value="" disabled>Field to set...</option>
                <option v-for="col in writableFields(action.relatedSpreadSheet)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title }}</option>
              </select>
              <span v-if="fieldIsLoggedInUser(action.relatedSpreadSheet, action.field)" class="sre-hint">The logged in user</span>
              <template v-else>
                <select v-model="action.valueSource" class="sre-select sre-select-sm">
                  <option value="static">Fixed value</option>
                  <option value="field">From a form field</option>
                </select>
                <select v-if="action.valueSource === 'field'" v-model="action.sourceField" class="sre-select sre-select-sm">
                  <option value="" disabled>Form field...</option>
                  <option v-for="col in sourceFieldOptions" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title }}</option>
                </select>
                <input
                  v-else
                  v-model="action.staticValue"
                  class="sre-input sre-input-sm"
                  type="text"
                  placeholder="value..."
                />
              </template>
              <span class="sre-hint">Updates every record linked through this relationship</span>
            </template>

            <template v-if="action.type === 'create_record'">
              <select v-model="action.spreadSheet" class="sre-select sre-select-sm" @change="onCreateSheetChange(action)">
                <option value="" disabled>Spreadsheet...</option>
                <option v-for="sheet in allSpreadsheets" :key="sheet.uniqueTitle" :value="sheet.uniqueTitle">{{ sheet.title || sheet.uniqueTitle }}</option>
              </select>
            </template>

            <template v-if="action.type === 'run_step_workflow'">
              <select v-model="action.workflowUniqueTitle" class="sre-select sre-select-sm">
                <option value="" disabled>Workflow...</option>
                <option v-for="workflow in stepWorkflows" :key="workflow.uniqueTitle" :value="workflow.uniqueTitle">{{ workflowLabel(workflow) }}</option>
              </select>
              <select v-model="action.recordSource" class="sre-select sre-select-sm">
                <option value="form">This form's record</option>
                <option value="related">A related record</option>
              </select>
              <select v-if="action.recordSource === 'related'" :value="relationshipKey(action)" class="sre-select sre-select-sm" @change="setRelationship(action, $event.target.value)">
                <option value="" disabled>Relationship...</option>
                <option v-for="target in relationshipTargets" :key="target.key" :value="target.key">{{ target.label }}</option>
              </select>
              <span class="sre-hint">The workflow starts from one record on its own spreadsheet. A related record runs once for each linked record, up to 25.</span>
              <p v-if="workflowWarning(action.workflowUniqueTitle)" class="workflow-overlap-warning">
                {{ workflowWarning(action.workflowUniqueTitle) }}
              </p>
            </template>

            <div v-if="showsAssignments(action)" class="sre-assignments">
              <div v-for="(assignment, vi) in action.values" :key="vi" class="sre-action-row">
                <select v-model="assignment.field" class="sre-select sre-select-sm" @change="onAssignmentFieldChange(action, assignment)">
                  <option value="" disabled>Field...</option>
                  <option v-for="col in assignmentFields(action)" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title }}</option>
                </select>
                <span v-if="assignmentIsLoggedInUser(action, assignment)" class="sre-hint">The logged in user</span>
                <template v-else>
                  <select v-model="assignment.valueSource" class="sre-select sre-select-sm">
                    <option value="static">Fixed value</option>
                    <option value="field">From a form field</option>
                  </select>
                  <select v-if="assignment.valueSource === 'field'" v-model="assignment.sourceField" class="sre-select sre-select-sm">
                    <option value="" disabled>Form field...</option>
                    <option v-for="col in sourceFieldOptions" :key="col.uniqueTitle" :value="col.uniqueTitle">{{ col.title }}</option>
                  </select>
                  <input
                    v-else
                    v-model="assignment.staticValue"
                    class="sre-input sre-input-sm"
                    type="text"
                    placeholder="value..."
                  />
                </template>
                <button class="sre-action-remove" @click="removeAssignment(action, vi)" title="Remove field">&times;</button>
              </div>
              <button class="sre-add-btn" @click="addAssignment(action)">+ add field</button>
            </div>

            <button class="sre-action-remove" @click="removeAction(ai)" title="Remove action">&times;</button>
          </div>

          <button class="sre-add-btn" @click="addAction">+ add action</button>
        </div>

        <!-- Edit buttons -->
        <div class="sre-edit-buttons">
          <button class="sre-btn sre-btn-save" @click="saveRule">{{ editingIndex !== null ? 'Update' : 'Add' }} Rule</button>
          <button class="sre-btn sre-btn-cancel" @click="cancelEdit">Cancel</button>
        </div>
      </div>

      <!-- Add rule button -->
      <div v-if="!isEditing" class="sre-add-rule-row">
        <button class="sre-add-btn" @click="startAddRule">+ add rule</button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { pageViews } from '../../../../shared/view-refresh.js'
import { ruleChainTypes } from '../../lib/rule-chain-types.js'
import {
  relationshipWriteTargets,
  assignmentColumns,
  copyableSourceFields
} from '../../../../shared/submission-record-actions.js'
import { findTopLevelLoginSpreadSheet } from '../../../../shared/logged-in-user-default.js'

const OPERATORS = {
  number: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: '>', label: 'greater than' },
    { key: '<', label: 'less than' },
    { key: '>=', label: 'greater or equal' },
    { key: '<=', label: 'less or equal' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  string: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'contains', label: 'contains' },
    { key: 'begins', label: 'starts with' },
    { key: 'ends', label: 'ends with' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  datetime: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'before', label: 'before' },
    { key: 'after', label: 'after' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  relationship: [
    { key: 'contains', label: 'contains' },
    { key: 'notContains', label: 'does not contain' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  checkbox: [
    { key: '=', label: 'equals' },
    { key: '!=', label: 'not equals' },
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ],
  signature: [
    { key: 'empty', label: 'is empty' },
    { key: 'notEmpty', label: 'is not empty' }
  ]
}

const SUBMISSION_ACTIONS = [
  { key: 'redirect_parent', label: 'Redirect to parent page' },
  { key: 'redirect_page', label: 'Redirect to a specific page' },
  { key: 'redirect_child', label: 'Redirect to a child page' },
  { key: 'clear_form', label: 'Clear / reset form' },
  { key: 'refresh_views', label: 'Refresh all views on page' },
  { key: 'refresh_view', label: 'Refresh a specific view' },
  { key: 'close_modal', label: 'Close modal' },
  { key: 'close_modal_reload', label: 'Close modal and reload the page behind it' },
  { key: 'set_related_value', label: 'Set a value on a related record' },
  { key: 'create_related', label: 'Create a related record' },
  { key: 'create_record', label: 'Create a record' },
  { key: 'run_step_workflow', label: 'Run a step workflow' }
]

let ruleIdCounter = 0

function generateRuleId () {
  return `srule_${Date.now()}_${++ruleIdCounter}`
}

export default {
  name: 'SubmissionRuleEditor',
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    spreadsheet: { type: Object, default: null },
    allSpreadsheets: { type: Array, default: () => [] },
    pages: { type: Array, default: () => [] },
    pageComponents: { type: Array, default: () => [] },
    currentPageUniqueTitle: { type: String, default: '' },
    applicationUniqueTitle: { type: String, default: '' }
  },
  data () {
    return {
      localVisible: false,
      localRules: [],
      isEditing: false,
      editingIndex: null,
      editType: 'if',
      editConditions: [],
      editActions: [],
      editMatchMode: 'all',
      stepWorkflows: [],
      workflowOverlaps: {}
    }
  },
  computed: {
    directColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns
    },
    relationshipColumns () {
      if (!this.spreadsheet || !this.spreadsheet.columns) return []
      return this.spreadsheet.columns.filter(c => c.type === 'relationship')
    },
    childPages () {
      return this.pages.filter(p => p.parentUniqueTitle === this.currentPageUniqueTitle)
    },
    currentPageLabel () {
      const current = this.pages.find(p => p.uniqueTitle === this.currentPageUniqueTitle)
      return current ? (current.title || current.uniqueTitle) : 'the current page'
    },
    availableTypes () {
      return ruleChainTypes(this.localRules, this.editingIndex)
    },
    submissionActionOptions () {
      return SUBMISSION_ACTIONS
    },
    pageViewOptions () {
      return pageViews(this.pageComponents)
    },
    relationshipTargets () {
      return relationshipWriteTargets(this.spreadsheet, this.allSpreadsheets)
    },
    sourceFieldOptions () {
      return copyableSourceFields(this.spreadsheet)
    },
    loginSpreadSheet () {
      return findTopLevelLoginSpreadSheet(this.pages)
    }
  },
  watch: {
    visible (val) {
      this.localVisible = val
      if (val) {
        this.localRules = JSON.parse(JSON.stringify(this.rules || []))
        this.cancelEdit()
        this.fetchStepWorkflows()
      }
    }
  },
  methods: {
    operatorsForDataType (dataType) {
      return OPERATORS[dataType] || OPERATORS.string
    },

    workflowLabel (workflow) {
      const sheet = (this.allSpreadsheets || []).find(item => item.uniqueTitle === workflow.spreadSheetUniqueTitle)
      const sheetName = sheet ? (sheet.title || sheet.uniqueTitle) : (workflow.spreadSheetUniqueTitle || '')
      return sheetName ? `${workflow.title || workflow.uniqueTitle} (${sheetName})` : (workflow.title || workflow.uniqueTitle)
    },

    async fetchStepWorkflows () {
      if (!this.applicationUniqueTitle) {
        this.stepWorkflows = []
        return
      }
      try {
        const response = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/step-workflows/`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })
        this.stepWorkflows = response.data || []
        await this.fetchWorkflowOverlaps()
      } catch (error) {
        this.stepWorkflows = []
      }
    },

    async fetchWorkflowOverlaps () {
      const sheet = this.spreadsheet && this.spreadsheet.uniqueTitle
      if (!this.applicationUniqueTitle || !sheet) {
        this.workflowOverlaps = {}
        return
      }
      try {
        const response = await this.$http.get(
          `${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${sheet}/workflow-overlaps`,
          { headers: { 'application-unique-title': this.applicationUniqueTitle } }
        )
        this.workflowOverlaps = response.data || {}
      } catch (error) {
        this.workflowOverlaps = {}
      }
    },

    workflowWarning (workflowUniqueTitle) {
      const entry = workflowUniqueTitle && this.workflowOverlaps[workflowUniqueTitle]
      return entry && entry.formWarning ? entry.formWarning : ''
    },

    isNoValueOp (op) {
      return op === 'empty' || op === 'notEmpty'
    },

    getColumnDataType (fieldName) {
      const systemTypeMap = { id: 'number', createdAt: 'datetime', updatedAt: 'datetime' }
      if (systemTypeMap[fieldName]) return systemTypeMap[fieldName]
      if (!this.spreadsheet || !this.spreadsheet.columns) return 'string'
      const col = this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName)
      if (!col) return 'string'
      const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', formula: 'string', password: 'string', richText: 'string', relationship: 'relationship', checkbox: 'checkbox', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
      return typeMap[col.type] || 'string'
    },

    getColumnForField (fieldName) {
      const systemColumns = [
        { uniqueTitle: 'id', title: 'ID', type: 'number', system: true },
        { uniqueTitle: 'createdAt', title: 'Created At', type: 'dateTime', system: true },
        { uniqueTitle: 'updatedAt', title: 'Updated At', type: 'dateTime', system: true }
      ]
      const systemCol = systemColumns.find(c => c.uniqueTitle === fieldName)
      if (systemCol) return systemCol
      if (!this.spreadsheet || !this.spreadsheet.columns) return null
      return this.spreadsheet.columns.find(c => c.uniqueTitle === fieldName) || null
    },

    condFieldIsRelationship (cond) {
      if (cond.dataType === 'relationship') return true
      if (cond.source === 'self' || cond.source === 'field') {
        return this.getColumnDataType(cond.field) === 'relationship'
      }
      return false
    },

    getCondRelatedSpreadSheet (cond) {
      const fieldName = cond.field
      if (!fieldName) return null
      const col = this.getColumnForField(fieldName)
      return col ? col.relatedSpreadSheet : null
    },

    onCondRelSearchInput (cond) {
      if (cond._relSearchTimeout) clearTimeout(cond._relSearchTimeout)
      cond._relSearchTimeout = setTimeout(() => this.searchCondRelRecords(cond), 250)
    },

    async searchCondRelRecords (cond) {
      const relSS = this.getCondRelatedSpreadSheet(cond)
      if (!relSS) return

      cond.relSearchLoading = true
      try {
        const headers = {}
        if (this.applicationUniqueTitle) {
          headers['application-unique-title'] = this.applicationUniqueTitle
        }
        const response = await fetch(
          `${VUE_APP_PROXY_SERVER_URL}/data/${relSS}/search?q=${encodeURIComponent(cond.relSearchQuery || '')}`,
          { headers }
        )
        const data = await response.json()
        const selectedIds = new Set((cond.relSelectedRecords || []).map(r => r.id))
        cond.relSearchResults = data.filter(r => !selectedIds.has(r.id))
      } catch (err) {
        console.error('relationship search error:', err)
        cond.relSearchResults = []
      } finally {
        cond.relSearchLoading = false
      }
    },

    selectCondRelRecord (cond, rec) {
      if (!cond.relSelectedRecords) cond.relSelectedRecords = []
      if (!cond.relSelectedRecords.find(r => r.id === rec.id)) {
        cond.relSelectedRecords.push({ id: rec.id, displayValue: rec.displayValue })
      }
      cond.relSearchResults = (cond.relSearchResults || []).filter(r => r.id !== rec.id)
      cond.relSearchQuery = ''
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    removeCondRelRecord (cond, id) {
      cond.relSelectedRecords = (cond.relSelectedRecords || []).filter(r => r.id !== id)
      cond.value = cond.relSelectedRecords.map(r => r.id)
    },

    getRelatedColumns (relColUniqueTitle) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === relColUniqueTitle)
      if (!relCol || !relCol.relatedSpreadSheet) return []
      const relSS = this.allSpreadsheets.find(s => s.uniqueTitle === relCol.relatedSpreadSheet)
      if (!relSS || !relSS.columns) return []
      return relSS.columns.filter(c => c.type !== 'relationship')
    },

    conditionSummary (cond) {
      if (cond.source === 'none') return 'always'
      if (cond.source === 'modal') return 'page is open in a modal'

      let fieldLabel = cond.field || '?'
      if (cond.source === 'related') {
        const q = cond.relatedQuantifier || 'any'
        const qLabel = q === 'none' ? 'none of' : q
        fieldLabel = `${qLabel} ${cond.relationshipColumn || '?'}.${cond.field || '?'}`
      }
      const opObj = this.operatorsForDataType(cond.dataType || 'string').find(o => o.key === cond.operator)
      const opLabel = opObj ? opObj.label : cond.operator
      if (this.isNoValueOp(cond.operator)) {
        return `${fieldLabel} ${opLabel}`
      }
      let displayValue = cond.value ?? ''
      if (this.condFieldIsRelationship(cond)) {
        const recs = cond.relSelectedRecords || []
        displayValue = recs.map(r => r.displayValue).join(', ') || '(none)'
      } else if (cond.dataType === 'checkbox') {
        displayValue = cond.value === 'true' ? 'Checked' : 'Unchecked'
      }
      return `${fieldLabel} ${opLabel} ${displayValue}`
    },

    actionSummary (action) {
      switch (action.type) {
        case 'redirect_parent':
          return 'Redirect to parent page'
        case 'redirect_page': {
          const page = this.pages.find(p => p.uniqueTitle === action.pageUniqueTitle)
          return `Redirect to ${page ? (page.title || page.uniqueTitle) : action.pageUniqueTitle || '?'}`
        }
        case 'redirect_child': {
          const page = this.pages.find(p => p.uniqueTitle === action.pageUniqueTitle)
          let label = `Redirect to child ${page ? (page.title || page.uniqueTitle) : action.pageUniqueTitle || '?'}`
          if (action.autoCreate) label += ' (auto-create)'
          return label
        }
        case 'clear_form': {
          let label = 'Clear form'
          if (action.showMessage && action.message) label += `: "${action.message}"`
          return label
        }
        case 'refresh_views':
          return 'Refresh all views on page'
        case 'refresh_view': {
          const view = this.pageViewOptions.find(item => item.uniqueTitle === action.viewUniqueTitle)
          return `Refresh ${view ? view.label : (action.viewUniqueTitle || 'view')}`
        }
        case 'close_modal':
          return 'Close modal'
        case 'close_modal_reload':
          return 'Close modal and reload the page behind it'
        case 'set_related_value': {
          const value = action.valueSource === 'loggedInUser'
            ? 'the logged in user'
            : action.valueSource === 'field'
              ? `from ${action.sourceField || '?'}`
              : (action.staticValue ?? '')
          return `Set ${this.relationshipLabel(action)} ${action.field || '?'} to ${value}`
        }
        case 'create_related':
          return `Create related ${this.relationshipLabel(action)}`
        case 'create_record': {
          const sheet = (this.allSpreadsheets || []).find(item => item.uniqueTitle === action.spreadSheet)
          return `Create ${sheet ? (sheet.title || sheet.uniqueTitle) : (action.spreadSheet || 'record')}`
        }
        case 'run_step_workflow': {
          const workflow = this.stepWorkflows.find(item => item.uniqueTitle === action.workflowUniqueTitle)
          const name = workflow ? (workflow.title || workflow.uniqueTitle) : (action.workflowUniqueTitle || 'workflow')
          return action.recordSource === 'related' ? `Run ${name} from a related record` : `Run ${name}`
        }
        default:
          return '?'
      }
    },

    relationshipLabel (action) {
      const target = this.relationshipTargets.find(item => item.key === this.relationshipKey(action))
      return target ? target.label : (action.relatedSpreadSheet || action.relationshipColumn || 'related record')
    },

    relationshipKey (action) {
      if (!action) return ''
      if (action.relatedSource === 'reverse' && action.sourceSpreadsheet && action.sourceRelationshipColumn) {
        return `reverse:${action.sourceSpreadsheet}:${action.sourceRelationshipColumn}`
      }
      if (action.relationshipColumn) return `forward:${action.relationshipColumn}`
      return ''
    },

    setRelationship (action, key) {
      const target = this.relationshipTargets.find(item => item.key === key)
      if (!target) return
      action.relatedSource = target.relatedSource
      action.relationshipColumn = target.relationshipColumn
      action.sourceSpreadsheet = target.sourceSpreadsheet
      action.sourceRelationshipColumn = target.sourceRelationshipColumn
      action.relatedSpreadSheet = target.relatedSpreadSheet
      action.field = ''
      action.values = []
    },

    writableFields (spreadSheetUniqueTitle) {
      const sheet = (this.allSpreadsheets || []).find(item => item.uniqueTitle === spreadSheetUniqueTitle)
      return assignmentColumns(sheet, this.loginSpreadSheet)
    },

    fieldIsLoggedInUser (spreadSheetUniqueTitle, field) {
      return this.writableFields(spreadSheetUniqueTitle).some(column => column.uniqueTitle === field && column.loggedInUser)
    },

    assignmentIsLoggedInUser (action, assignment) {
      const sheet = action.type === 'create_record' ? action.spreadSheet : action.relatedSpreadSheet
      return this.fieldIsLoggedInUser(sheet, assignment && assignment.field)
    },

    onTargetFieldChange (action) {
      if (this.fieldIsLoggedInUser(action.relatedSpreadSheet, action.field)) {
        action.valueSource = 'loggedInUser'
        action.staticValue = ''
        action.sourceField = ''
        return
      }
      if (action.valueSource === 'loggedInUser') action.valueSource = 'static'
    },

    onAssignmentFieldChange (action, assignment) {
      if (this.assignmentIsLoggedInUser(action, assignment)) {
        assignment.valueSource = 'loggedInUser'
        assignment.staticValue = ''
        assignment.sourceField = ''
        return
      }
      if (assignment.valueSource === 'loggedInUser') assignment.valueSource = 'static'
    },

    assignmentFields (action) {
      if (action.type === 'create_record') return this.writableFields(action.spreadSheet)
      return this.writableFields(action.relatedSpreadSheet)
    },

    showsAssignments (action) {
      if (action.type === 'create_record') return !!action.spreadSheet
      if (action.type === 'create_related') return !!action.relatedSpreadSheet
      return false
    },

    onCreateSheetChange (action) {
      action.values = []
    },

    addAssignment (action) {
      if (!Array.isArray(action.values)) action.values = []
      action.values.push({ field: '', valueSource: 'static', staticValue: '', sourceField: '' })
    },

    removeAssignment (action, index) {
      action.values.splice(index, 1)
    },

    // Condition source changes
    onCondSourceChange (cond) {
      cond.field = null
      cond.relationshipColumn = null
      cond.relatedSpreadsheet = null
      cond.relatedQuantifier = 'any'
      cond.operator = '='
      cond.value = ''
    },

    onCondFieldChange (cond) {
      cond.dataType = this.getColumnDataType(cond.field)
      cond.operator = this.operatorsForDataType(cond.dataType)[0]?.key || '='
    },

    onCondRelColChange (cond) {
      const relCol = (this.spreadsheet?.columns || []).find(c => c.uniqueTitle === cond.relationshipColumn)
      if (relCol) {
        cond.relatedSpreadsheet = relCol.relatedSpreadSheet
      }
      cond.field = null
    },

    onActionTypeChange (action) {
      action.pageUniqueTitle = ''
      action.viewUniqueTitle = ''
      action.autoCreate = false
      action.showMessage = false
      action.message = ''
      action.newPageTitle = ''
      action.newPageUniqueTitle = ''
      action.newPageUniqueTitleManual = false
      action.createError = ''
      action.relatedSource = 'forward'
      action.relationshipColumn = ''
      action.sourceSpreadsheet = ''
      action.sourceRelationshipColumn = ''
      action.relatedSpreadSheet = ''
      action.spreadSheet = ''
      action.field = ''
      action.valueSource = 'static'
      action.staticValue = ''
      action.sourceField = ''
      action.values = []
      action.workflowUniqueTitle = ''
      action.recordSource = 'form'
    },

    onAutoCreateToggle (action) {
      action.createError = ''
      if (action.autoCreate && !action.pageUniqueTitle && !action.newPageTitle) {
        this.prefillNewChildPage(action)
      }
    },

    onNewChildPageTitleInput (action) {
      if (!action.newPageUniqueTitleManual) {
        action.newPageUniqueTitle = this.sanitizeToUniqueTitle(action.newPageTitle || '')
      }
    },

    prefillNewChildPage (action) {
      const current = this.pages.find(p => p.uniqueTitle === this.currentPageUniqueTitle)
      const parentTitle = current ? (current.title || current.uniqueTitle) : 'page'
      action.newPageTitle = `${parentTitle} detail`
      action.newPageUniqueTitle = this.uniqueChildTitle(this.sanitizeToUniqueTitle(action.newPageTitle))
      action.newPageUniqueTitleManual = false
    },

    sanitizeToUniqueTitle (title) {
      return String(title || '')
        .toLowerCase()
        .replace(/[^a-z0-9\s_]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
    },

    uniqueChildTitle (base) {
      const taken = new Set(this.pages.map(p => p.uniqueTitle))
      let candidate = base || 'child_page'
      let n = 2
      while (taken.has(candidate)) {
        candidate = `${base || 'child_page'}_${n}`
        n++
      }
      return candidate
    },

    async createChildPage (action) {
      const title = String(action.newPageTitle || '').trim()
      const uniqueTitle = String(action.newPageUniqueTitle || '').trim()

      if (!this.currentPageUniqueTitle) {
        action.createError = 'This page can only be created from the page you are editing.'
        return false
      }

      if (!title || !uniqueTitle) {
        action.createError = 'A title and unique title are required to create the sub-page.'
        return false
      }

      action.createError = ''

      try {
        const created = await this.$http.post(`${VUE_APP_PROXY_SERVER_URL}/pages/`, {
          title,
          uniqueTitle,
          parentUniqueTitle: this.currentPageUniqueTitle
        }, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })

        const page = created.data
        if (!this.pages.some(p => p.uniqueTitle === page.uniqueTitle)) {
          this.pages.push(page)
        }

        action.pageUniqueTitle = page.uniqueTitle

        if (window.design && window.design.getPages) {
          await window.design.getPages()
        }

        return true
      } catch (error) {
        const message = (error.response && error.response.data) || error.message || 'Failed to create sub-page'
        action.createError = typeof message === 'string' ? message : 'Failed to create sub-page'
        return false
      }
    },

    // Rule CRUD
    startAddRule () {
      this.isEditing = true
      this.editingIndex = null
      this.editType = this.availableTypes[0]
      this.editConditions = [this.newCondition()]
      this.editActions = [this.newAction()]
      this.editMatchMode = 'all'
    },

    editRule (idx) {
      const rule = this.localRules[idx]
      this.isEditing = true
      this.editingIndex = idx
      this.editType = rule.type
      this.editConditions = JSON.parse(JSON.stringify(rule.conditions || [])).map(c => ({
        ...c,
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false,
        relSelectedRecords: c.relSelectedRecords || []
      }))
      this.editActions = JSON.parse(JSON.stringify(rule.actions || []))
      for (const action of this.editActions) {
        if (!Array.isArray(action.values)) action.values = []
        if (action.type === 'redirect_child' && action.autoCreate && !action.pageUniqueTitle && !action.newPageTitle) {
          this.prefillNewChildPage(action)
        }
      }
      this.editMatchMode = rule.conditionMatchMode || 'all'
    },

    async saveRule () {
      for (const action of this.editActions) {
        if (action.type === 'redirect_child' && action.autoCreate && !action.pageUniqueTitle) {
          const created = await this.createChildPage(action)
          if (!created) return false
        }
      }

      const cleanConditions = (this.editType === 'else' ? [] : this.editConditions).map(c => {
        const clean = { ...c }
        delete clean.relSearchQuery
        delete clean.relSearchResults
        delete clean.relSearchLoading
        delete clean._relSearchTimeout
        return clean
      })
      const rule = {
        id: this.editingIndex !== null ? this.localRules[this.editingIndex].id : generateRuleId(),
        type: this.editType,
        conditions: cleanConditions,
        conditionMatchMode: this.editMatchMode,
        actions: this.editActions.filter(a => a.type).map(a => this.cleanAction(a))
      }

      if (this.editingIndex !== null) {
        this.localRules.splice(this.editingIndex, 1, rule)
      } else {
        const lastIdx = this.localRules.length - 1
        if (lastIdx >= 0 && this.localRules[lastIdx].type === 'else' && rule.type !== 'else') {
          this.localRules.splice(lastIdx, 0, rule)
        } else {
          this.localRules.push(rule)
        }
      }

      this.cancelEdit()
      return true
    },

    cleanAction (action) {
      const clean = { ...action }
      delete clean.newPageTitle
      delete clean.newPageUniqueTitle
      delete clean.newPageUniqueTitleManual
      delete clean.createError
      if (Array.isArray(clean.values)) {
        clean.values = clean.values.filter(item => item && item.field).map(item => {
          if (item.valueSource === 'loggedInUser') return { field: item.field, valueSource: 'loggedInUser' }
          return {
            field: item.field,
            valueSource: item.valueSource === 'field' ? 'field' : 'static',
            staticValue: item.staticValue == null ? '' : item.staticValue,
            sourceField: item.sourceField || ''
          }
        })
      }
      return clean
    },

    removeRule (idx) {
      this.localRules.splice(idx, 1)
      if (this.editingIndex === idx) {
        this.cancelEdit()
      } else if (this.editingIndex !== null && this.editingIndex > idx) {
        this.editingIndex--
      }
    },

    cancelEdit () {
      this.isEditing = false
      this.editingIndex = null
      this.editConditions = []
      this.editActions = []
    },

    // Condition/Action helpers
    newCondition () {
      return {
        source: 'self',
        field: null,
        operator: '=',
        value: '',
        dataType: 'string',
        relationshipColumn: null,
        relatedSpreadsheet: null,
        relatedQuantifier: 'any',
        relSelectedRecords: [],
        relSearchQuery: '',
        relSearchResults: [],
        relSearchLoading: false
      }
    },

    addCondition () {
      this.editConditions.push(this.newCondition())
    },

    removeCondition (idx) {
      this.editConditions.splice(idx, 1)
    },

    newAction () {
      return {
        type: '',
        pageUniqueTitle: '',
        viewUniqueTitle: '',
        autoCreate: false,
        showMessage: false,
        message: '',
        newPageTitle: '',
        newPageUniqueTitle: '',
        newPageUniqueTitleManual: false,
        createError: '',
        relatedSource: 'forward',
        relationshipColumn: '',
        sourceSpreadsheet: '',
        sourceRelationshipColumn: '',
        relatedSpreadSheet: '',
        spreadSheet: '',
        field: '',
        valueSource: 'static',
        staticValue: '',
        sourceField: '',
        values: [],
        workflowUniqueTitle: '',
        recordSource: 'form'
      }
    },

    addAction () {
      this.editActions.push(this.newAction())
    },

    removeAction (idx) {
      this.editActions.splice(idx, 1)
    },

    // Save/Cancel modal
    async onSave (event) {
      if (event && event.preventDefault) event.preventDefault()

      if (this.isEditing) {
        const saved = await this.saveRule()
        if (!saved) return
      }

      this.$emit('update:rules', this.localRules)
      this.$emit('update:visible', false)
      this.localVisible = false
    },

    onHide () {
      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.workflow-overlap-warning {
  color: #8a5a12;
  font-size: 0.85rem;
  margin: 0.35rem 0 0;
}
.sre-container {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #141412;
  padding: 12px;
}

/* Rule list */
.sre-rule-list {
  margin-bottom: 10px;
}

.sre-connector {
  display: flex;
  align-items: center;
  margin: 4px 0;
  padding: 0 4px;
}
.sre-connector-line {
  flex: 1;
  height: 1px;
  background: #2e2e2b;
}

/* Rule item */
.sre-rule-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.12s;
}
.sre-rule-open {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  padding: 0;
  cursor: pointer;
}
.sre-rule-item:hover {
  border-color: #474842;
  background: #1e1e1b;
}
.sre-rule-item.sre-rule-editing {
  border-color: #9ad12b;
}

/* Type badge */
.sre-type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
  height: 22px;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  flex-shrink: 0;
  text-align: center;
  padding: 0 6px;
}
.sre-type-if {
  background: #1e2e12;
  color: #9ad12b;
}
.sre-type-elseif {
  background: #2e2512;
  color: #d0a05b;
}
.sre-type-else {
  background: #251e30;
  color: #a05bd0;
}

/* Rule body */
.sre-rule-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
}
.sre-rule-conditions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.sre-cond-text {
  color: #e8e8e6;
  font-size: 0.78rem;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
}
.sre-cond-join {
  color: #a8a8a4;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.sre-cond-empty {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-style: italic;
}
.sre-rule-arrow {
  color: #a8a8a4;
  font-size: 0.85rem;
  flex-shrink: 0;
}
.sre-rule-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.sre-action-pill {
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  height: 20px;
  border-radius: 3px;
  font-size: 0.7rem;
  font-weight: 600;
  background: #1a2a3a;
  color: #5ba0d0;
  border: 1px solid #2a3a4a;
}
.sre-action-empty {
  color: #a8a8a4;
  font-size: 0.75rem;
  font-style: italic;
}

/* Remove button */
.sre-rule-remove, .sre-cond-remove, .sre-action-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: transparent;
  border: 1px solid transparent;
  color: #a8a8a4;
  font-size: 16px;
  line-height: 1;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;
}
.sre-rule-remove:hover, .sre-cond-remove:hover, .sre-action-remove:hover {
  color: #f82471;
  border-color: #f82471;
  background: rgba(248, 36, 113, 0.1);
}

/* Empty state */
.sre-empty {
  color: #a8a8a4;
  font-size: 0.82rem;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

/* Edit form */
.sre-edit-form {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  background: #1a1a17;
  padding: 14px;
  margin-top: 10px;
}
.sre-edit-title {
  color: #e8e8e6;
  font-size: 0.88rem;
  font-weight: 600;
  margin-bottom: 12px;
}

.sre-form-row {
  margin-bottom: 10px;
}
.sre-form-label {
  display: block;
  color: #7a7a78;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 4px;
}

.sre-form-section {
  border: 1px solid #2e2e2b;
  border-radius: 5px;
  padding: 10px;
  margin-bottom: 10px;
  background: #141412;
}
.sre-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.sre-section-title {
  color: #b0b0ae;
  font-size: 0.78rem;
  font-weight: 600;
}

/* Match mode toggle */
.sre-match-toggle {
  display: flex;
  gap: 4px;
}
.sre-mt-btn {
  background: #1a1a17;
  border: 1px solid #2e2e2b;
  color: #7a7a78;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.06em;
}
.sre-mt-btn:hover {
  border-color: #474842;
  color: #b0b0ae;
}
.sre-mt-btn.active {
  background: #1e2e12;
  border-color: #9ad12b;
  color: #9ad12b;
}

/* Condition row */
.sre-condition-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

/* Action row */
.sre-action-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

/* Form controls */
.sre-select {
  background: #1e1e1b;
  border: 1px solid #474842;
  color: #e8e8e6;
  font-size: 0.78rem;
  padding: 5px 8px;
  border-radius: 4px;
  outline: none;
  min-width: 100px;
}
.sre-select:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15);
}
.sre-select-sm {
  padding: 3px 6px;
  font-size: 0.75rem;
}

.sre-input {
  background: #1e1e1b;
  border: 1px solid #474842;
  color: #e8e8e6;
  font-size: 0.78rem;
  padding: 5px 8px;
  border-radius: 4px;
  outline: none;
  min-width: 80px;
}
.sre-input:focus {
  border-color: #9ad12b;
  box-shadow: 0 0 0 1px rgba(154, 209, 43, 0.15);
}
.sre-input-sm {
  padding: 3px 6px;
  font-size: 0.75rem;
}
.sre-input-wide {
  min-width: 200px;
  flex: 1;
}

.sre-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #b0b0ae;
  font-size: 0.78rem;
  cursor: pointer;
  white-space: nowrap;
}

.sre-hint {
  color: #7a7a78;
  font-size: 0.72rem;
}

.sre-hint-error {
  color: #f82471;
}

.sre-assignments {
  flex-basis: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 2px 0 4px 8px;
  padding-left: 8px;
  border-left: 2px solid #2e2e2b;
}

/* Add button */
.sre-add-btn {
  color: #9ad12b;
  background: transparent;
  border: 1px dashed #474842;
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
}
.sre-add-btn:hover {
  color: #b8f046;
  background: #1e1e1b;
  border-color: #9ad12b;
}

.sre-add-rule-row {
  margin-top: 8px;
}

/* Edit buttons */
.sre-edit-buttons {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}
.sre-btn {
  padding: 5px 16px;
  border-radius: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid;
}
.sre-btn-save {
  background: #1e2e12;
  border-color: #9ad12b;
  color: #9ad12b;
}
.sre-btn-save:hover {
  background: #2a3e1a;
}
.sre-btn-cancel {
  background: transparent;
  border-color: #474842;
  color: #7a7a78;
}
.sre-btn-cancel:hover {
  border-color: #6a6a67;
  color: #b0b0ae;
}

/* Quantifier selector */
.sre-quantifier {
  min-width: 80px;
  font-weight: 600;
  color: #d0a05b;
}

/* Select option styling (for dark mode) */
.sre-select option {
  background: #1e1e1b;
  color: #e8e8e6;
}

/* Relationship picker in conditions */
.sre-rel-picker {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 180px;
}
.sre-rel-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.sre-rel-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #2a2a27;
  border: 1px solid #474842;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 0.72rem;
  color: #c8c8c6;
}
.sre-rel-tag-remove {
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0 2px;
  line-height: 1;
}
.sre-rel-tag-remove:hover {
  color: #e44;
}
.sre-rel-search-wrap {
  position: relative;
}
.sre-rel-loading {
  font-size: 0.7rem;
  color: #888;
  padding: 2px 4px;
}
.sre-rel-results {
  background: #1e1e1b;
  border: 1px solid #474842;
  border-radius: 4px;
  max-height: 120px;
  overflow-y: auto;
}
.sre-rel-result-item {
  padding: 4px 8px;
  font-size: 0.75rem;
  color: #c8c8c6;
  cursor: pointer;
}
.sre-rel-result-item:hover {
  background: #333;
  color: #9ad12b;
}
</style>
